import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from '../locales/en.json';
import tr from '../locales/tr.json';
import { translateTraderText } from '../locales/translate';

const translations = { en, tr };
const readLanguage = () => { try { return localStorage.getItem('trader_language') === 'tr' ? 'tr' : 'en'; } catch { return 'en'; } };
const lookup = (catalogue, key) => key.split('.').reduce((value, part) => value && typeof value === 'object' ? value[part] : undefined, catalogue);
const TraderLanguageContext = createContext(null);

export const TraderLanguageProvider = ({ children }) => {
  const [language, updateLanguage] = useState(readLanguage);
  useEffect(() => {
    try { localStorage.setItem('trader_language', language); } catch { /* Private storage may be unavailable. */ }
    const previous = { lang: document.documentElement.lang, dir: document.documentElement.dir };
    document.documentElement.lang = language;
    document.documentElement.dir = 'ltr';
    return () => { document.documentElement.lang = previous.lang; document.documentElement.dir = previous.dir; };
  }, [language]);
  useEffect(() => {
    const synchronize = event => { if (event.key === 'trader_language') updateLanguage(readLanguage()); };
    window.addEventListener('storage', synchronize);
    return () => window.removeEventListener('storage', synchronize);
  }, []);
  const setLanguage = useCallback(next => { if (translations[next]) updateLanguage(next); }, []);
  const toggleLanguage = useCallback(() => updateLanguage(current => current === 'en' ? 'tr' : 'en'), []);
  const tx = useCallback((text, variables) => translateTraderText(text, language, variables), [language]);
  const t = useCallback((key, variables) => {
    const value = lookup(translations[language], key) ?? lookup(en, key);
    return typeof value === 'string' ? value.replace(/\{(\w+)\}/g, (match, name) => variables?.[name] ?? match) : tx(key, variables);
  }, [language, tx]);
  const value = useMemo(() => ({ language, locale: language === 'tr' ? 'tr-TR' : 'en-US', t, tx, toggleLanguage, setLanguage, setLang: setLanguage }), [language, t, tx, toggleLanguage, setLanguage]);
  return <TraderLanguageContext.Provider value={value}>{children}</TraderLanguageContext.Provider>;
};

export const useTraderLanguage = () => {
  const context = useContext(TraderLanguageContext);
  if (!context) throw new Error('useTraderLanguage must be used within TraderLanguageProvider');
  return context;
};

export const useTraderText = () => useTraderLanguage().tx;